/**
 * Catálogo cerrado de errores de la API (SDD SaaS §8.2). La web y la extensión
 * traducen `code` a texto; `message` es un texto en español apto para mostrar
 * si no hay traducción específica. El detalle del proveedor nunca viaja aquí.
 */

export type ErrorCode =
  // Genéricos
  | 'validation_error'
  | 'unauthorized'
  | 'forbidden'
  | 'account_suspended'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'payload_too_large'
  | 'client_upgrade_required'
  | 'internal_error'
  | 'service_unavailable'
  // Grabaciones
  | 'recording_not_recording'
  | 'recording_already_active'
  | 'recording_too_long'
  | 'chunk_seq_out_of_range'
  | 'chunk_too_large'
  | 'track_invalid'
  | 'audio_unavailable'
  // Importaciones
  | 'import_type_not_allowed'
  | 'import_too_large'
  | 'import_signature_mismatch'
  | 'import_daily_limit'
  // Notas
  | 'summary_version_limit'
  | 'summary_daily_limit'
  | 'ask_daily_limit'
  | 'correction_daily_limit'
  | 'template_limit'
  | 'webhook_limit'
  | 'tracker_limit'
  | 'send_daily_limit'
  | 'webhook_url_invalid'
  | 'summary_rejected'
  // Cuota y cobro
  | 'quota_exceeded'
  | 'plan_not_available'
  | 'billing_webhook_invalid'
  // Compartir
  | 'share_disabled'
  | 'share_expired'
  // Sesión de la extensión
  | 'extension_link_invalid'

export interface ApiErrorBody {
  error: {
    code: ErrorCode
    message: string
    details?: unknown
    requestId?: string
  }
}

/** Códigos HTTP por defecto para cada código de error. */
export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  validation_error: 400,
  unauthorized: 401,
  forbidden: 403,
  account_suspended: 403,
  not_found: 404,
  conflict: 409,
  rate_limited: 429,
  payload_too_large: 413,
  client_upgrade_required: 426,
  internal_error: 500,
  service_unavailable: 503,
  recording_not_recording: 409,
  recording_already_active: 409,
  recording_too_long: 422,
  chunk_seq_out_of_range: 422,
  chunk_too_large: 413,
  track_invalid: 422,
  audio_unavailable: 409,
  import_type_not_allowed: 415,
  import_too_large: 413,
  import_signature_mismatch: 422,
  import_daily_limit: 429,
  summary_version_limit: 429,
  summary_daily_limit: 429,
  ask_daily_limit: 429,
  correction_daily_limit: 429,
  template_limit: 403,
  webhook_limit: 403,
  tracker_limit: 403,
  send_daily_limit: 429,
  webhook_url_invalid: 422,
  summary_rejected: 422,
  quota_exceeded: 402,
  plan_not_available: 422,
  billing_webhook_invalid: 400,
  share_disabled: 403,
  share_expired: 404,
  extension_link_invalid: 401
}

/** Mensajes por defecto en español (es-CL). */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  validation_error: 'Los datos enviados no son válidos.',
  unauthorized: 'Tienes que iniciar sesión.',
  forbidden: 'No tienes permiso para hacer esto.',
  account_suspended:
    'Tu cuenta está suspendida. Si crees que es un error, escríbenos a hola@holaeco.cl.',
  not_found: 'No encontramos lo que buscas.',
  conflict: 'La operación choca con el estado actual.',
  rate_limited: 'Demasiadas solicitudes. Espera un momento.',
  payload_too_large: 'El contenido es demasiado grande.',
  client_upgrade_required: 'Actualiza la extensión de eco para continuar.',
  internal_error: 'Algo falló de nuestro lado. Ya lo estamos revisando.',
  service_unavailable: 'El servicio no está disponible por ahora.',
  recording_not_recording: 'La grabación ya no está en curso.',
  recording_already_active: 'Ya tienes una grabación en curso.',
  recording_too_long: 'La grabación supera la duración máxima permitida.',
  chunk_seq_out_of_range: 'Número de trozo fuera de rango.',
  chunk_too_large: 'El trozo de audio es demasiado grande.',
  track_invalid: 'Pista de audio no válida.',
  audio_unavailable:
    'El audio ya fue eliminado; puedes regenerar las notas o reimportar tu copia local.',
  import_type_not_allowed: 'Tipo de archivo no permitido.',
  import_too_large: 'El archivo supera el tamaño permitido por tu plan.',
  import_signature_mismatch: 'El archivo no coincide con el tipo declarado.',
  import_daily_limit: 'Alcanzaste el límite diario de importaciones de tu plan.',
  summary_version_limit: 'Ya tienes el máximo de versiones de notas para esta plantilla.',
  summary_daily_limit: 'Alcanzaste el límite diario de regeneraciones de tu plan.',
  ask_daily_limit: 'Alcanzaste el límite diario de preguntas de tu plan. Vuelve mañana.',
  correction_daily_limit:
    'Alcanzaste el límite diario de correcciones de tu plan. Vuelve mañana o edita las notas a mano.',
  template_limit:
    'Llegaste al máximo de plantillas propias de tu plan. Borra una o mejora tu plan.',
  webhook_limit: 'Tu plan no incluye más integraciones. Mejora tu plan para agregar otra.',
  tracker_limit: 'Llegaste al máximo de temas a seguir de tu plan.',
  send_daily_limit: 'Llegaste al máximo de envíos de notas por hoy. Vuelve mañana.',
  webhook_url_invalid: 'La URL debe ser https y pública (no puede apuntar a una red interna).',
  summary_rejected: 'No pudimos generar notas para esta reunión.',
  quota_exceeded:
    'No te quedan horas en tu plan. La grabación se guardó y se procesará cuando mejores tu plan.',
  plan_not_available: 'Ese plan todavía no está disponible.',
  billing_webhook_invalid: 'Webhook de cobro inválido.',
  share_disabled: 'Compartir está desactivado en tu organización.',
  share_expired: 'Este enlace ya no está disponible.',
  extension_link_invalid: 'El enlace para conectar la extensión no es válido o venció.'
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (!value || typeof value !== 'object') return false
  const e = (value as { error?: unknown }).error
  return !!e && typeof e === 'object' && typeof (e as { code?: unknown }).code === 'string'
}
